import { cloneElement, isValidElement, ReactElement, ReactNode } from "react";
import { useTranslation } from "react-i18next";
import {
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  SelectProps,
  MenuProps,
  makeStyles,
  OutlinedInput,
} from "@material-ui/core";

// Minimal shape of an element returned by `renderOption` that we need to read
// and write for auto-injecting `data-testid`. Intentionally not tied to
// `MenuItemProps` — callers may return any component, not just MUI MenuItem.
type OptionElementProps = {
  value?: unknown;
  "data-testid"?: string;
};

export interface Params<T> extends SelectProps {
  options: Array<T>;
  renderOption: (option: T, index: number, array: Array<T>) => ReactNode;
  allowEmpty?: boolean;
  emptyOption?: string;
  renderEndAdornment?: () => ReactElement;
  error?: boolean;
  menuTitle?: string;
  helperText?: string;
  "data-testid"?: string;
}

export default function SelectField<T>({
  label,
  options,
  menuTitle = "",
  helperText = "",
  allowEmpty = false,
  emptyOption,
  renderOption,
  renderValue,
  renderEndAdornment,
  fullWidth = true,
  "data-testid": dataTestId,
  ...rest
}: Params<T>) {
  const { t } = useTranslation("common");
  const classes = useStyles();

  const handleRenderValue: SelectProps["renderValue"] = value => {
    if (!value && allowEmpty) {
      return <>{emptyOption || t("none")}</>;
    }
    return renderValue(value);
  };

  // Auto-inject a derived `data-testid` on the element returned by `renderOption`,
  // unless the caller already set one or the element has no `value`.
  // Active only when the SelectField itself received a `data-testid` prop.
  const injectTestId = (el: ReactNode): ReactNode => {
    if (!isValidElement(el)) return el;

    const element = el as ReactElement<OptionElementProps>;
    if (element.props["data-testid"]) return el;

    const value = element.props.value;
    if (value == null) return el;

    return cloneElement(element, {
      "data-testid": `${dataTestId}-option-${value}`,
    });
  };

  const renderOptionWithTestId = (opt: T, index: number, arr: Array<T>) => {
    const optionEl = renderOption(opt, index, arr);

    if (!dataTestId) return optionEl;

    if (Array.isArray(optionEl)) {
      return optionEl.map(injectTestId);
    }

    return injectTestId(optionEl);
  };

  const { SelectDisplayProps: callerDisplayProps, ...restWithoutDisplay } =
    rest;

  const selectDisplayProps = {
    ...(dataTestId && { "data-testid": dataTestId }),
    ...callerDisplayProps,
  };

  return (
    <FormControl variant="outlined" fullWidth={fullWidth}>
      {label && (
        <InputLabel
          htmlFor={rest.name}
          error={Boolean(rest.error)}
          {...(allowEmpty && { shrink: true })}
        >
          {label}
        </InputLabel>
      )}
      <Select
        MenuProps={menuProps}
        endAdornment={renderEndAdornment && renderEndAdornment()}
        renderValue={renderValue && handleRenderValue}
        displayEmpty={allowEmpty}
        SelectDisplayProps={selectDisplayProps}
        input={
          <OutlinedInput
            {...(label && { label })}
            {...(allowEmpty && { notched: true })}
          />
        }
        {...restWithoutDisplay}
      >
        {!!menuTitle && (
          <MenuItem value="" disabled>
            <em>{menuTitle}</em>
          </MenuItem>
        )}
        {allowEmpty && (
          <MenuItem
            value=""
            data-testid={dataTestId ? `${dataTestId}-option-NONE` : undefined}
          >
            {emptyOption || (t("none") as string)}
          </MenuItem>
        )}
        {!!options?.length && options.map(renderOptionWithTestId)}
      </Select>
      {helperText && (
        <FormHelperText className={classes.helperText} error={rest.error}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
}

const useStyles = makeStyles({
  helperText: {
    fontSize: "11px",
    marginLeft: "5px",
    position: "absolute",
    top: 55,
    left: 3,
  },
});

const menuProps: Partial<MenuProps> = {
  getContentAnchorEl: null,
  anchorOrigin: {
    vertical: "bottom",
    horizontal: "left",
  },
  transformOrigin: {
    vertical: "top",
    horizontal: "left",
  },
};
